import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TablePagination from "@dashboard/components/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink";
import {
  WsmErrorFragment,
  WsmFitmentApplicationFragment,
  WsmFitmentQualifierFragment,
  WsmFitmentSchemaFragment,
} from "@dashboard/graphql";
import { renderCollection } from "@dashboard/misc";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton, Text, TrashBinIcon } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { getWsmFitmentErrorMessage } from "../errors";
import { useWsmFitmentAttempt } from "../hooks/useWsmFitmentAttempt";
import { WsmFitmentPagingProps } from "../hooks/useWsmFitmentPagedValues";
import { applicationMessages } from "../messages";
import { WsmFitmentWriteResult } from "../types";
import { wsmFitmentSchemaUrl } from "../urls";
import {
  formatApplication,
  formatQualifierValues,
  getApplicationCells,
  getFitmentLabel,
  getSchemaAxes,
} from "../utils";
import {
  WsmFitmentApplicationAddDialog,
  WsmFitmentApplicationDraft,
} from "./WsmFitmentApplicationAddDialog";

export interface WsmFitmentApplicationsPageProps {
  schema: WsmFitmentSchemaFragment;
  applications: WsmFitmentApplicationFragment[] | undefined;
  totalCount?: number | null;
  paging: WsmFitmentPagingProps;
  qualifiers: WsmFitmentQualifierFragment[];
  disabled: boolean;
  /** Resolves to how this one add came back; the dialog shows those errors only. */
  onAdd: (draft: WsmFitmentApplicationDraft) => Promise<WsmFitmentWriteResult>;
  onDelete: (id: string) => Promise<WsmFitmentWriteResult>;
}

/** The applications of one schema: a column per axis in ask order, then the qualifiers. */
export const WsmFitmentApplicationsPage = ({
  schema,
  applications,
  totalCount,
  paging,
  qualifiers,
  disabled,
  onAdd,
  onDelete,
}: WsmFitmentApplicationsPageProps) => {
  const intl = useIntl();
  const [addOpen, setAddOpen] = useState(false);
  const [toDelete, setToDelete] = useState<WsmFitmentApplicationFragment | null>(null);
  // Each dialog shows its last call's errors only, cleared when it opens.
  const [addErrors, setAddErrors] = useState<WsmErrorFragment[]>([]);
  const [deleteError, setDeleteError] = useState<WsmErrorFragment | undefined>();
  // The delete button follows this dialog's own attempt, reset on open.
  const deleteAttempt = useWsmFitmentAttempt();
  const axes = getSchemaAxes(schema);
  const numberOfColumns = axes.length + 2;

  return (
    <ListPageLayout>
      <TopNav
        href={wsmFitmentSchemaUrl(schema.id)}
        title={intl.formatMessage(applicationMessages.title, { schema: getFitmentLabel(schema) })}
        subtitle={
          typeof totalCount === "number"
            ? intl.formatMessage(applicationMessages.count, { count: totalCount })
            : undefined
        }
      >
        <Button
          disabled={disabled || axes.length === 0}
          onClick={() => {
            setAddErrors([]);
            setAddOpen(true);
          }}
          data-test-id="wsm-fitment-application-add"
        >
          {intl.formatMessage(applicationMessages.add)}
        </Button>
      </TopNav>
      <DashboardCard data-test-id="wsm-fitment-application-list">
        <DashboardCard.Content paddingX={0}>
          <ResponsiveTable>
            <TableHead>
              <TableRowLink>
                {axes.map(axis => (
                  <TableCell key={axis.id}>{getFitmentLabel(axis)}</TableCell>
                ))}
                <TableCell>{intl.formatMessage(applicationMessages.colQualifiers)}</TableCell>
                <TableCell />
              </TableRowLink>
            </TableHead>
            <TableBody>
              {renderCollection(
                applications,
                row => (
                  <TableRowLink
                    key={row ? row.id : "skeleton"}
                    data-test-id="wsm-fitment-application-row"
                  >
                    {(row ? getApplicationCells(row, axes) : axes.map(() => "")).map(
                      (cell, index) => (
                        <TableCell key={axes[index].id}>
                          {!row && <Skeleton />}
                          {row && cell}
                          {row && !cell && (
                            <Text size={2} color="default2">
                              {intl.formatMessage(applicationMessages.any)}
                            </Text>
                          )}
                        </TableCell>
                      ),
                    )}
                    <TableCell>
                      {row ? formatQualifierValues(row.qualifierValues) || "-" : <Skeleton />}
                    </TableCell>
                    <TableCell align="right">
                      {row && (
                        <Box display="flex" justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="tertiary"
                            icon={<TrashBinIcon />}
                            title={intl.formatMessage(applicationMessages.deleteTitle)}
                            disabled={disabled}
                            onClick={() => {
                              setDeleteError(undefined);
                              deleteAttempt.reset();
                              setToDelete(row);
                            }}
                            data-test-id="wsm-fitment-application-delete"
                          />
                        </Box>
                      )}
                    </TableCell>
                  </TableRowLink>
                ),
                () => (
                  <TableRowLink data-test-id="wsm-fitment-application-list-empty">
                    <TableCell colSpan={numberOfColumns}>
                      <Text size={3} color="default2">
                        {intl.formatMessage(applicationMessages.empty)}
                      </Text>
                    </TableCell>
                  </TableRowLink>
                ),
              )}
            </TableBody>
            <TableFooter>
              <TableRowLink>
                <TablePagination
                  colSpan={numberOfColumns}
                  settings={{ rowNumber: paging.rowNumber }}
                  onUpdateListSettings={(_, value) => paging.onRowNumberChange(value as number)}
                  hasNextPage={paging.hasNextPage}
                  hasPreviousPage={paging.hasPreviousPage}
                  onNextPage={paging.onNextPage}
                  onPreviousPage={paging.onPreviousPage}
                />
              </TableRowLink>
            </TableFooter>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>
      <WsmFitmentApplicationAddDialog
        open={addOpen}
        axes={axes}
        qualifiers={qualifiers}
        errors={addErrors}
        disabled={disabled}
        onClose={() => setAddOpen(false)}
        onConfirm={async draft => {
          const added = await onAdd(draft);

          setAddErrors(added.errors);

          if (added.saved) {
            setAddOpen(false);
          }

          return added.saved;
        }}
      />
      <ActionDialog
        open={!!toDelete}
        variant="delete"
        title={intl.formatMessage(applicationMessages.deleteTitle)}
        confirmButtonState={deleteAttempt.state}
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) {
            return;
          }

          const deleted = await deleteAttempt.run(() => onDelete(toDelete.id));

          setDeleteError(deleted.errors[0]);

          if (deleted.saved) {
            setToDelete(null);
          }
        }}
      >
        <Text display="block">
          {intl.formatMessage(applicationMessages.deleteContent, {
            name: toDelete ? formatApplication(toDelete, axes) : "",
          })}
        </Text>
        {deleteError && (
          <Text
            display="block"
            color="critical1"
            marginTop={2}
            data-test-id="wsm-fitment-application-delete-error"
          >
            {getWsmFitmentErrorMessage(deleteError, intl)}
          </Text>
        )}
      </ActionDialog>
    </ListPageLayout>
  );
};

export default WsmFitmentApplicationsPage;
